/**
 * "Så fungerar Regta" — onboarding/instructions page reached from the
 * Översikt card. Plain narrative walkthrough; no live data.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, FileCheck, FileUp, ClipboardCheck, BellRing } from 'lucide-react';
import { usePageTitle } from '../use-page-title.js';

const HB = '#1A3D2B';

type Step = {
  icon: typeof FileText;
  title: string;
  body: string;
  href?: string;
  cta?: string;
};

const steps: Step[] = [
  {
    icon: FileText,
    title: 'Välj en mall',
    body:
      'Mallbiblioteket innehåller juridiskt granskade svenska hyresavtal — bostadshyra, lokalhyra, indexavtal, förhandlingsordning och fler. Mallarna underhålls centralt så du alltid får senaste versionen.',
    href: '/templates',
    cta: 'Öppna mallbiblioteket',
  },
  {
    icon: FileCheck,
    title: 'Skapa och fyll i avtalet',
    body:
      'Avtalet drafts från mallen och öppnas i editorn. Fyll i hyresgäst, fastighet och nyckeldatum — fält som redan finns i Regta autofyller direkt.',
    href: '/contracts',
    cta: 'Mina avtal',
  },
  {
    icon: FileUp,
    title: 'Importera befintliga avtal',
    body:
      'Har du redan avtal på pappersform eller i PDF? Ladda upp dem så strukturerar Regta innehållet automatiskt — enstaka filer eller hela bestånd i bulk.',
    href: '/contracts/import',
    cta: 'Importera',
  },
  {
    icon: ClipboardCheck,
    title: 'Skicka för granskning och signering',
    body:
      'Skicka avtalet på intern granskning innan det går ut, och låt sedan parterna signera digitalt via Scrive. Status uppdateras i realtid.',
    href: '/reviews',
    cta: 'Granskningar',
  },
  {
    icon: BellRing,
    title: 'Bevaka avtalets livscykel',
    body:
      'Regta håller koll på förhandlingsordning, uppsägning och lokalavtal. Påminnelser skickas 3 månader, 1 månad och 1 vecka före nyckeldatum så ingen deadline missas.',
  },
];

export function HowItWorksPage() {
  usePageTitle('Så fungerar Regta');

  return (
    <article className="max-w-3xl space-y-12">
      <div>
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-stone-400 hover:text-stone-600"
        >
          <ArrowLeft className="h-3 w-3" />
          Tillbaka till översikten
        </Link>
        <h1 className="font-display text-3xl font-light tracking-tight mt-4 text-stone-900">
          Så fungerar Regta
        </h1>
        <p className="text-sm text-stone-500 leading-relaxed mt-3 max-w-xl">
          Regta samlar hela kedjan från avtalsskapande till bevakning på ett
          ställe. Här är en snabb genomgång av flödet — följ stegen i
          ordning första gången, men i vardagen hoppar du in där det
          behövs.
        </p>
      </div>

      <ol className="space-y-10">
        {steps.map((step, i) => {
          const Icon = step.icon;
          return (
            <li key={step.title} className="grid grid-cols-[auto_1fr] gap-6">
              <div
                className="flex h-10 w-10 items-center justify-center text-white"
                style={{ backgroundColor: HB }}
              >
                <span className="text-sm font-medium">{i + 1}</span>
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <Icon className="h-4 w-4 text-stone-400" />
                  <h2 className="font-display text-xl font-light text-stone-900">
                    {step.title}
                  </h2>
                </div>
                <p className="text-sm text-stone-500 leading-relaxed mt-2">
                  {step.body}
                </p>
                {step.href && step.cta && (
                  <Link
                    to={step.href}
                    className="inline-block mt-3 text-sm hover:underline"
                    style={{ color: HB }}
                  >
                    {step.cta} →
                  </Link>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </article>
  );
}
